import { useState, useEffect, useMemo, useCallback } from "react";

import { Formik, Form } from "formik";
import * as Yup from 'yup';

import useTranslation from "../../../hooks/useTranslation";

import TEXT from "../../../configs/text";

import REQUESTS from "../../../api/requests";

import Input from "../../../components/Input";
import Button from "../../../components/Button";
import Message from "../../../components/Message";

import styles from "../styles/PlaylistModal.module.scss";

export default function PlaylistByUrl({ playlist, mac, getPlaylists }) {
    const [loading, setLoading] = useState(false);

    const [errorMessage, setErrorMessage] = useState({
        text: null,
        type: false
    });

    const translation = useTranslation();

    const handleText = (text) => {
        if (translation?.pages?.playlist?.[text]) {
            return translation?.pages?.playlist?.[text];
        }

        return TEXT?.pages?.playlist?.[text];
    };

    const validationSchema = Yup.object({
        name: Yup.string().required(handleText("playlist_name_message")),
        url: Yup.string().required(handleText("playlist_url_message")).matches(/^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i, handleText("Please enter a valid URL!")),
        is_protected: Yup.boolean(),
        pin: Yup.string().test('pin', handleText("Pin is required!"), function (value) {
            if (this.parent.is_protected) {
                return value ? true : false;
            }

            return true;
        }),
        confirm_pin: Yup.string().test('confirm_pin', handleText("Confirm Pin is required!"), function (value) {
            if (this.parent.is_protected) {
                return value ? true : false;
            }
            return true;
        }).test('confirm_pin', handleText("Pins must match!"), function (value) {
            if (this.parent.is_protected) {
                return value === this.parent.pin;
            }
            return true;
        })
    });

    const onSubmit = (values, { resetForm }) => {
        const { name, url, pin, confirm_pin, epg_url, is_protected } = values;

        setLoading(true);

        setErrorMessage({
            text: null,
            type: false
        });

        const formData = new FormData();

        formData.append("name", name);
        formData.append("mac", mac);
        formData.append("is_protected", is_protected);

        if (url) {
            formData.append("url", url.split(" ").join(""));
        }

        if (is_protected) {
            formData.append("pin", pin);
            formData.append("confirm_pin", confirm_pin);
        }

        if (epg_url) {
            formData.append("epg_url", epg_url.split(" ").join(""));
        }

        function callback() {
            setLoading(false);
            getPlaylists();
            resetForm();
        }

        function errorCallback(error) {
            setErrorMessage({
                text: error.message,
                type: false
            });
            setLoading(false);
        }

        REQUESTS.UPLOAD_PLAYLIST(formData, callback, errorCallback);
    };

    const editPlaylist = (values, { resetForm }) => {
        const { name, url, epg_url, is_protected, pin, confirm_pin } = values;

        setLoading(true);

        setErrorMessage({
            text: null,
            type: false
        });

        const body = {
            id: playlist.id,
            name: name,
            url,
        };

        if (epg_url) {
            body["epg_url"] = epg_url
        }

        if (playlist.is_protected && is_protected) {
            body["pin"] = pin;
            body["new_pin"] = pin;
            body["confirm_pin"] = confirm_pin;
        } else if (playlist.is_protected && !is_protected) {
            body["pin"] = pin;
            body["is_protected"] = is_protected;
        } else if (pin && confirm_pin && is_protected && !playlist.is_protected) {
            body["new_pin"] = pin;
            body["confirm_pin"] = confirm_pin;
            body["is_protected"] = is_protected;
        }

        function callback(data) {
            getPlaylists();
            resetForm();
            setLoading(false);
        }

        function errorCallback(error) {
            setErrorMessage({
                text: error.message,
                type: false
            });
            setLoading(false);
        }

        REQUESTS.PLAYLIST.EDIT(body, callback, errorCallback);
    }

    return (
        <Formik
            initialValues={{
                name: playlist?.name || "",
                url: playlist?.url || "",
                epg_url: playlist?.epg_url || "",
                pin: playlist?.pin || "",
                confirm_pin: playlist?.pin || "",
                is_protected: playlist?.is_protected || false
            }}
            validationSchema={validationSchema}
            validateOnBlur={false}
            destroyOnUnmount={true}
            onSubmit={playlist ? editPlaylist : onSubmit}
        >
            {({ values, errors, touched, resetForm, setFieldValue }) => {
                return <Form autoComplete="on">
                    <Input
                        name="name"
                        placeholder={handleText("playlist_name")}
                        message={{
                            text: touched.name && errors.name,
                            type: false
                        }}
                    />
                    <Input
                        name="url"
                        placeholder={handleText("playlist_url")}
                        message={{
                            text: touched.url && errors.url,
                            type: false
                        }}
                    />
                    <Input
                        name="epg_url"
                        placeholder={handleText("playlist_epg_url")}
                    />

                    <div className={styles['protected-checkbox']}>
                        <Input
                            name="is_protected"
                            type="checkbox"
                            label={handleText("protect_playlist")}
                        />
                    </div>

                    <Input
                        type="password"
                        name="pin"
                        disabled={!values.is_protected}
                        placeholder={handleText("set_pin")}
                        message={{
                            text: touched.pin && errors.pin,
                            type: false
                        }}
                    />
                    <Input
                        type="password"
                        name="confirm_pin"
                        placeholder={handleText("confirm_pin")}
                        disabled={!values.is_protected}
                        message={{
                            text: touched.confirm_pin && errors.confirm_pin,
                            type: false
                        }}
                    />
                    <div className={styles['err-message-container']}><Message message={errorMessage} style={{ fontSize: 16 }} /></div>
                    <Button loading={loading} disabled={loading} type="submit">{handleText(playlist ? "save" : "url_button_title")}</Button>
                </Form>
            }}
        </Formik >
    )
}
